import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import QRCode from "qrcode";
import { api } from "../lib/api";
import { Aviso, Cargando } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";

/**
 * Carnets para imprimir, con el mismo QR que lleva el portal.
 *
 * El portal ya da el carnet en el móvil, pero muchos socios no se instalan
 * nada, y los clubes imprimen tarjetas. Esta página compone las tarjetas a
 * tamaño de tarjeta de crédito (85,6 × 54 mm, el de cualquier carnet) y deja
 * al navegador imprimirlas o guardarlas en PDF: ocho por hoja A4, cortadas con
 * la guillotina o pegadas en una tarjeta de plástico. Sin librería de PDF en
 * el servidor, igual que el libro de socios.
 *
 * El QR es el código de acceso del socio, el mismo que enseña el portal y el
 * que lee el mostrador: da igual que venga de la tarjeta o del móvil.
 */

interface Carnet {
  id: string;
  numeroSocio: number;
  nombre: string;
  apellidos: string;
  estado: string;
  fotoUrl: string | null;
  fechaAlta: string;
  fechaCaducidad: string | null;
  codigoAcceso: string;
  tipoSocio: { nombre: string; color: string } | null;
}

interface Respuesta {
  club: {
    nombre: string;
    cif: string | null;
    telefono: string | null;
    direccion: string | null;
    ciudad: string | null;
    logoUrl: string | null;
  };
  socios: Carnet[];
}

/** «03/2027»: en una tarjeta cabe el mes, no el día, y es lo que se mira. */
function mesAnio(fecha: string | null): string | null {
  if (!fecha) return null;
  const d = new Date(fecha);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function CodigoQR({ texto }: { texto: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let vigente = true;
    void QRCode.toString(texto, {
      type: "svg",
      margin: 0,
      errorCorrectionLevel: "M",
    }).then((s) => {
      if (vigente) setSvg(s);
    });
    return () => {
      vigente = false;
    };
  }, [texto]);
  return svg ? (
    <div className="carnet-qr" dangerouslySetInnerHTML={{ __html: svg }} />
  ) : (
    <div className="carnet-qr" />
  );
}

export default function Carnets() {
  const { t, cuenta } = useIdioma();
  const [parametros] = useSearchParams();
  const ids = parametros.get("ids") ?? "";
  const todos = !ids;

  const datos = useQuery({
    queryKey: ["carnets", ids],
    queryFn: () =>
      api<Respuesta>("/socios/carnets", {
        query: todos ? { estado: "ACTIVO" } : { ids },
      }),
  });

  if (datos.isLoading) return <Cargando texto={t("cn.preparando")} />;
  if (!datos.data) return <Aviso tipo="error">{t("cn.falloPreparar")}</Aviso>;
  const { club, socios } = datos.data;

  return (
    <div>
      <div className="sin-imprimir fila separada" style={{ marginBottom: 16 }}>
        <Link
          to={
            todos || socios.length !== 1
              ? "/socios"
              : `/socios/${socios[0]!.id}`
          }
          className="boton secundario"
        >
          ← {t("cn.volver")}
        </Link>
        <button
          className="boton"
          onClick={() => window.print()}
          disabled={socios.length === 0}
        >
          {t("lb.imprimir")}
        </button>
      </div>

      <p className="sin-imprimir suave pequeno">
        {t(todos ? "cn.cuantosTodos" : "cn.cuantos", {
          carnets: cuenta(socios.length, "cn.unCarnet", "cn.carnets"),
        })}{" "}
        {t("cn.comoImprimir")} {t("cn.elQr")}
      </p>

      {socios.length === 0 ? (
        <Aviso tipo="info">{t("cn.ninguno")}</Aviso>
      ) : (
        <article className="documento hoja-carnets">
          {socios.map((s) => {
            const hasta = mesAnio(s.fechaCaducidad);
            return (
              <div key={s.id} className="carnet">
                <div className="carnet-cabecera">
                  <strong>{club.nombre}</strong>
                  <span className="carnet-numero">{t("cn.numero", { numero: s.numeroSocio })}</span>
                </div>
                <div className="carnet-cuerpo">
                  {s.fotoUrl ? (
                    <img className="carnet-foto" src={s.fotoUrl} alt="" />
                  ) : (
                    <div className="carnet-foto carnet-foto-vacia">
                      {s.nombre.charAt(0)}
                      {s.apellidos.charAt(0)}
                    </div>
                  )}
                  <div className="carnet-datos">
                    <div className="carnet-nombre">
                      {s.nombre}
                      <br />
                      {s.apellidos}
                    </div>
                    {s.tipoSocio && (
                      <span
                        className="carnet-tipo"
                        style={{
                          borderColor: s.tipoSocio.color,
                          color: s.tipoSocio.color,
                        }}
                      >
                        {s.tipoSocio.nombre}
                      </span>
                    )}
                    <div className="carnet-validez">
                      {hasta
                        ? t("cn.validoHasta", { fecha: hasta })
                        : t("cn.socioDesde", { fecha: mesAnio(s.fechaAlta) ?? "" })}
                    </div>
                  </div>
                  <div className="carnet-lado">
                    <CodigoQR texto={s.codigoAcceso} />
                    <span className="carnet-codigo">{s.codigoAcceso}</span>
                  </div>
                </div>
                <div className="carnet-pie">
                  {[
                    club.cif && `CIF ${club.cif}`,
                    club.telefono && `Tel. ${club.telefono}`,
                  ]
                    .filter(Boolean)
                    .join(" · ") || t("cn.documentoInterno")}
                </div>
              </div>
            );
          })}
        </article>
      )}
    </div>
  );
}
